// ─── E2EE primitives for "My Cheques" personal account ───────────────────────
//
// Design doc: docs/design/e2ee-my-cheques.md
//
// Model:
//   - Each passkey user holds one symmetric AES-256-GCM "User Master Key" (UMK).
//   - UMK is generated client-side, never persisted in plaintext, and wrapped
//     independently for each WebAuthn credential using a key derived from the
//     credential's PRF (hmac-secret) output.
//   - Cheque credentials {urlKey, pin} are encrypted with UMK and stored on the
//     server as opaque blobs in user_cheques.credentials_ciphertext.
//   - Server cannot decrypt anything in this module's output.
//
// All functions operate on Uint8Array; callers handle base64url I/O at the
// persistence boundary (see uint8ArrayToBase64Url in src/lib/crypto.ts).

import { uint8ArrayToBase64Url, base64UrlToUint8Array } from "./crypto";

/** Version constants for forward-compat. Bumped when the wire format changes. */
export const UMK_WRAP_VERSION = 1;
export const CREDENTIALS_VERSION = 1;

/** Fixed PRF salts — stable strings so the same passkey derives the same key forever. */
const PRF_SALT_UMK = new TextEncoder().encode("postcash/e2ee/umk-wrap/v1");

/** HKDF info contexts to domain-separate derived keys from the same PRF output. */
const HKDF_INFO_WRAP = new TextEncoder().encode("postcash/e2ee/wrap-key/v1");

const AES_NONCE_BYTES = 12;
const UMK_BYTES = 32;

// ─── UMK generation ──────────────────────────────────────────────────────────

/** Generate a fresh 256-bit User Master Key. Caller must hold it in memory only. */
export function generateUmk(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(UMK_BYTES));
}

/** Import raw UMK bytes as a non-extractable AES-GCM CryptoKey. */
export async function importUmk(umk: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    umk as unknown as ArrayBuffer,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// ─── PRF → wrap key derivation ───────────────────────────────────────────────

/**
 * Derive the AES-GCM wrap key for a given PRF output using HKDF-SHA256.
 * PRF output is 32 bytes from the authenticator (hmac-secret extension).
 */
export async function deriveWrapKey(prfOutput: Uint8Array): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    prfOutput as unknown as ArrayBuffer,
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(32) as unknown as ArrayBuffer, // zero-filled per RFC 5869 §3.1
      info: HKDF_INFO_WRAP as unknown as ArrayBuffer,
    },
    ikm,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// ─── UMK wrap / unwrap ───────────────────────────────────────────────────────

export interface WrappedBlob {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
  version: number;
}

/** Encrypt the UMK under a wrap key derived from a credential's PRF output. */
export async function wrapUmk(umk: Uint8Array, wrapKey: CryptoKey): Promise<WrappedBlob> {
  const nonce = crypto.getRandomValues(new Uint8Array(AES_NONCE_BYTES));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as ArrayBuffer },
    wrapKey,
    umk as unknown as ArrayBuffer,
  );
  return { ciphertext: new Uint8Array(ct), nonce, version: UMK_WRAP_VERSION };
}

/** Recover the UMK from a wrapped blob. Throws if tag invalid or key wrong. */
export async function unwrapUmk(blob: WrappedBlob, wrapKey: CryptoKey): Promise<Uint8Array> {
  if (blob.version !== UMK_WRAP_VERSION) {
    throw new Error(`unsupported umk_wrap_version=${blob.version}`);
  }
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: blob.nonce as unknown as ArrayBuffer },
    wrapKey,
    blob.ciphertext as unknown as ArrayBuffer,
  );
  const umk = new Uint8Array(pt);
  if (umk.length !== UMK_BYTES) {
    throw new Error(`unexpected umk length=${umk.length}`);
  }
  return umk;
}

// ─── Cheque credential encryption ────────────────────────────────────────────

export interface ChequeCredentials {
  urlKey: string;
  pin: string;
}

export interface EncryptedCredentials {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
  version: number;
}

/** Encrypt {urlKey, pin} for one cheque under the user's UMK. */
export async function encryptCredentials(
  umk: Uint8Array,
  creds: ChequeCredentials,
): Promise<EncryptedCredentials> {
  const key = await importUmk(umk);
  const nonce = crypto.getRandomValues(new Uint8Array(AES_NONCE_BYTES));
  const payload = new TextEncoder().encode(JSON.stringify(creds));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as ArrayBuffer },
    key,
    payload as unknown as ArrayBuffer,
  );
  return { ciphertext: new Uint8Array(ct), nonce, version: CREDENTIALS_VERSION };
}

/** Decrypt stored credentials blob using the user's UMK. */
export async function decryptCredentials(
  umk: Uint8Array,
  blob: EncryptedCredentials,
): Promise<ChequeCredentials> {
  if (blob.version !== CREDENTIALS_VERSION) {
    throw new Error(`unsupported credentials_version=${blob.version}`);
  }
  const key = await importUmk(umk);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: blob.nonce as unknown as ArrayBuffer },
    key,
    blob.ciphertext as unknown as ArrayBuffer,
  );
  const decoded = JSON.parse(new TextDecoder().decode(pt)) as ChequeCredentials;
  if (typeof decoded?.urlKey !== "string" || typeof decoded?.pin !== "string") {
    throw new Error("decrypted credentials have unexpected shape");
  }
  return decoded;
}

// ─── WebAuthn PRF ────────────────────────────────────────────────────────────

/** Shape of the PRF extension output we care about. */
type PrfExtensionResults = {
  prf?: {
    results?: {
      first?: ArrayBuffer;
    };
  };
};

/**
 * Feature-detect PRF support. Chrome/Edge ≥116 expose
 * `PublicKeyCredential.getClientCapabilities()`; Safari does not. In Safari we
 * optimistically return true and let the actual assertion be the source of
 * truth (if PRF is unsupported, the authenticator omits the prf extension
 * results and callers should fall back).
 */
export async function detectPrfSupport(): Promise<boolean> {
  const pk = (globalThis as { PublicKeyCredential?: unknown }).PublicKeyCredential as
    | (typeof PublicKeyCredential & {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
      })
    | undefined;
  if (!pk) return false;
  try {
    if (typeof pk.getClientCapabilities === "function") {
      const caps = await pk.getClientCapabilities();
      if (typeof caps?.extension_prf === "boolean") return caps.extension_prf;
    }
  } catch {
    // fall through to optimistic path
  }
  return true;
}

/** The PRF salt we use to derive the UMK wrap key. Stable across calls. */
export function umkWrapSalt(): Uint8Array {
  return PRF_SALT_UMK.slice();
}

/**
 * Perform a WebAuthn assertion with the PRF extension, constrained to the
 * given credential IDs. Returns the 32-byte PRF output or null if the
 * authenticator didn't honor the extension.
 */
export async function assertPrf(
  credentialIds: Uint8Array[],
  salt: Uint8Array,
  challenge: Uint8Array,
  rpId?: string,
): Promise<Uint8Array | null> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: challenge as unknown as ArrayBuffer,
      rpId,
      allowCredentials: credentialIds.map((id) => ({
        type: "public-key" as const,
        id: id as unknown as ArrayBuffer,
      })),
      userVerification: "preferred",
      extensions: { prf: { eval: { first: salt as unknown as ArrayBuffer } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;
  if (!assertion) return null;
  const extResults = assertion.getClientExtensionResults() as PrfExtensionResults;
  const first = extResults?.prf?.results?.first;
  if (!first) return null;
  return new Uint8Array(first);
}

/**
 * Extract PRF output from a `navigator.credentials.create()` response when the
 * platform supports PRF-on-create (iOS 18+, Chrome ≥116). Returns null when the
 * authenticator omits prf results — caller should then perform a follow-up
 * assertPrf() call with the newly-registered credential.
 */
export function extractPrfFromRegistration(cred: PublicKeyCredential): Uint8Array | null {
  const ext = cred.getClientExtensionResults() as PrfExtensionResults;
  const first = ext?.prf?.results?.first;
  return first ? new Uint8Array(first) : null;
}

// ─── Base64url codecs for wire transport ─────────────────────────────────────

export function encodeWrappedBlob(blob: WrappedBlob): { wrappedUmk: string; umkNonce: string; version: number } {
  return {
    wrappedUmk: uint8ArrayToBase64Url(blob.ciphertext),
    umkNonce: uint8ArrayToBase64Url(blob.nonce),
    version: blob.version,
  };
}

export function decodeWrappedBlob(wire: { wrappedUmk: string; umkNonce: string; version: number }): WrappedBlob {
  return {
    ciphertext: base64UrlToUint8Array(wire.wrappedUmk),
    nonce: base64UrlToUint8Array(wire.umkNonce),
    version: wire.version,
  };
}

export function encodeCredentialsBlob(
  blob: EncryptedCredentials,
): { ciphertext: string; nonce: string; version: number } {
  return {
    ciphertext: uint8ArrayToBase64Url(blob.ciphertext),
    nonce: uint8ArrayToBase64Url(blob.nonce),
    version: blob.version,
  };
}

export function decodeCredentialsBlob(
  wire: { ciphertext: string; nonce: string; version: number },
): EncryptedCredentials {
  return {
    ciphertext: base64UrlToUint8Array(wire.ciphertext),
    nonce: base64UrlToUint8Array(wire.nonce),
    version: wire.version,
  };
}

/** Securely wipe a Uint8Array in-place. Best-effort — JS engines may copy. */
export function wipe(buf: Uint8Array): void {
  buf.fill(0);
}
